import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Clock, BookOpen, CheckCircle, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || '';

const AssessmentRules = () => {
  const navigate  = useNavigate();
  const [cfg, setCfg]         = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError]     = useState('');

  const attemptId = parseInt(localStorage.getItem('finirio_assess_id') || '0');
  const token     = localStorage.getItem('finirio_assess_tok') || '';
  const name      = localStorage.getItem('finirio_assess_name') || 'Candidate';

  useEffect(() => {
    if (!attemptId || !token) { navigate('/assessment'); return; }
    axios.get(`${API}/api/assessment/config-public`)
      .then(r => setCfg(r.data))
      .catch(() => setError('Could not load test configuration. Please refresh.'));
  }, [navigate]);

  const startTest = async () => {
    if (!accepted || starting) return;
    setStarting(true); setError('');
    try {
      const res = await axios.post(`${API}/api/assessment/start`, {
        attempt_id: attemptId, session_token: token,
      });
      localStorage.setItem('finirio_assess_exp',       res.data.expires_at);
      localStorage.setItem('finirio_assess_qs',        JSON.stringify(res.data.questions));
      localStorage.setItem('finirio_assess_saved_ans', JSON.stringify(res.data.saved_answers || {}));
      localStorage.setItem('finirio_assess_saved_rev', JSON.stringify(res.data.saved_reviews || {}));
      navigate('/assessment/test');
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not start test. Please try again.');
      setStarting(false);
    }
  };

  if (!cfg && !error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <p className="text-slate-500">Loading test details…</p>
    </div>
  );

  if (error && !cfg) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <p className="text-red-600 mb-3">{error}</p>
        <button onClick={() => window.location.reload()} className="text-[#1e73be] underline text-sm">Reload</button>
      </div>
    </div>
  );

  const sections = [
    { label: 'English',              count: cfg.english_count,  emoji: '📝' },
    { label: 'Mathematics',          count: cfg.maths_count,    emoji: '🔢' },
    { label: 'Logical Reasoning',    count: cfg.logical_count,  emoji: '🧠' },
    { label: 'Reading Comprehension', count: cfg.reading_count, emoji: '📖' },
  ].filter(s => s.count > 0);

  const total    = sections.reduce((a, s) => a + s.count, 0);
  const maxScore = total * cfg.positive_marks;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mini header */}
      <div className="bg-white border-b border-slate-200 py-3 px-6">
        <div className="max-w-[900px] mx-auto flex items-center justify-between">
          <Link to="/">
            <img src="https://customer-assets.emergentagent.com/job_site-creator-3034/artifacts/pbfdilsv_Finirio.JPG"
              alt="Finirio" className="h-8 object-contain" />
          </Link>
          <span className="text-sm text-slate-500">Aptitude Assessment</span>
        </div>
      </div>

      <div className="max-w-[900px] mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">

          {/* Header band */}
          <div className="p-7 text-white" style={{ background: 'linear-gradient(135deg,#0f3d78,#1e73be)' }}>
            <p className="text-white/70 text-sm mb-0.5">Welcome, {name}</p>
            <h1 className="text-2xl font-bold">Finirio Aptitude Assessment</h1>
            <p className="text-white/60 text-sm mt-1">Please read all instructions carefully before proceeding.</p>
          </div>

          {/* Stats strip */}
          <div className="grid grid-cols-3 divide-x divide-slate-100 bg-slate-50 border-b border-slate-100">
            {[
              { icon: <Clock className="w-4 h-4" />, label: 'Duration',    value: `${cfg.duration_minutes} Minutes` },
              { icon: <BookOpen className="w-4 h-4" />, label: 'Questions', value: `${total} Questions` },
              { icon: <CheckCircle className="w-4 h-4" />, label: 'Max Score', value: `${maxScore} Marks` },
            ].map(({ icon, label, value }) => (
              <div key={label} className="p-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center text-[#1e73be]"
                  style={{ backgroundColor: '#eaf3fb' }}>{icon}</div>
                <div>
                  <p className="text-[11px] text-slate-500">{label}</p>
                  <p className="font-semibold text-slate-800 text-sm">{value}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Sections & Marking */}
            <div>
              <h3 className="font-semibold text-slate-800 mb-3">Test Sections</h3>
              <div className="space-y-2 mb-4">
                {sections.map(s => (
                  <div key={s.label} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm text-slate-700">{s.emoji} {s.label}</span>
                    <span className="text-sm font-semibold text-slate-800">{s.count} Qs</span>
                  </div>
                ))}
              </div>
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                <p className="text-xs font-semibold text-blue-800 mb-2">📊 Marking Scheme</p>
                <div className="space-y-1 text-xs text-blue-700">
                  <p>✅ Correct answer: <strong>+{cfg.positive_marks} mark</strong></p>
                  <p>❌ Wrong answer: <strong>−{cfg.negative_marks} mark</strong> (negative marking)</p>
                  <p>⬜ Unanswered: <strong>0 marks</strong></p>
                </div>
              </div>
            </div>

            {/* Rules */}
            <div>
              <h3 className="font-semibold text-slate-800 mb-3">Important Instructions</h3>
              <ol className="space-y-2">
                {[
                  `Timer starts the moment you click "Start Test" — you have exactly ${cfg.duration_minutes} minutes.`,
                  'The test will auto-submit when time expires. You cannot extend time.',
                  'Do NOT close or refresh the browser. Your answers are auto-saved.',
                  'If you accidentally close the page, open the same link and continue — the timer will still be running.',
                  cfg.can_go_back ? 'You can freely navigate between questions.' : 'You cannot go back to a previous question.',
                  cfg.can_mark_review ? 'You can mark questions for review and revisit them before submitting.' : 'Review marking is disabled.',
                  'Each candidate may attempt the test only ONCE. Duplicate attempts are blocked by your mobile number.',
                  'Use Chrome or Firefox on a desktop/laptop for the best experience.',
                  'Ensure a stable internet connection throughout the test.',
                ].map((rule, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                    <span className="text-[#1e73be] font-bold text-xs mt-0.5 shrink-0">{i + 1}.</span>
                    <span>{rule}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {/* Footer / CTA */}
          <div className="p-6 border-t bg-slate-50">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0" />{error}
              </div>
            )}

            <label className="flex items-start gap-3 cursor-pointer mb-4">
              <input type="checkbox" checked={accepted} onChange={e => setAccepted(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-[#1e73be] rounded" />
              <span className="text-sm text-slate-700">
                I have read and understood all the instructions above. I agree to attempt the test honestly and understand that any malpractice may lead to disqualification.
              </span>
            </label>

            <button onClick={startTest} disabled={!accepted || starting}
              className="w-full h-13 py-3 rounded-xl text-white font-semibold text-base transition hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: accepted ? '#1e73be' : '#94a3b8' }}>
              {starting ? 'Starting Test…' : '🚀 Start Test Now'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentRules;
